import { Check, Send } from "lucide-react";

import { LiveDesk, PlanDesk, ScheduleDesk } from "@/features/platform/components/desks";
import { DirectoryMap, NextClientFace, PassesArt } from "@/features/platform/components/pictures";
import {
  ClientLiveScreen,
  ClientPlanScreen,
  ClientScreen,
  EventsScreen,
  FinanceScreen,
  TodayScreen,
} from "@/features/platform/components/app-screens";
import { FloatChip, SoonBadge, Stage } from "@/features/platform/components/stage";
import { Laptop, Phone, Watch } from "@/features/platform/components/devices";
import { Accent, displayTitle, Eyebrow, GoLink, Section } from "@/features/platform/components/landing-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/**
 * The words that open `/features`, inside the head card, and the coach's desk under them: the
 * week on a laptop and the day on a phone beside it, both running off the card's bottom edge.
 */
export const FeaturesHero = () => (
  <div className="relative">
    <div className="relative mx-auto max-w-[900px] px-1 pt-14 text-center md:pt-20">
      <Eyebrow>{m.platform_nav_features()}</Eyebrow>
      <h1 className={cn(displayTitle, "mt-6")}>
        {m.features_hero_title_lead()} <Accent>{m.features_hero_title_tail()}</Accent>
      </h1>
      <p className="mx-auto mt-6 max-w-[56ch] text-lg leading-[1.55] text-pretty text-muted-foreground">
        {m.features_hero_lede()}
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <GoLink to="/signup" tone="accent">
          {m.platform_nav_start()}
        </GoLink>
      </div>
    </div>
    <div className="relative mx-auto mt-14 -mb-8 h-[300px] max-w-[1060px] overflow-hidden sm:h-[420px] md:mt-16 md:-mb-11 md:h-[560px]">
      <span className="absolute inset-x-[10%] bottom-0 h-3/4 rounded-full bg-[radial-gradient(closest-side,var(--site-glow),transparent)] blur-2xl" />
      <Laptop className="absolute top-6 left-1/2 w-[min(860px,118%)] -translate-x-[54%]">
        <ScheduleDesk />
      </Laptop>
      <Phone className="absolute top-[70px] left-[calc(50%+min(270px,26vw))] w-[min(250px,30vw)] -translate-x-1/2">
        <TodayScreen />
      </Phone>
      <FloatChip className="top-[18%] left-[calc(50%-440px)] max-lg:hidden">
        <Check className="size-4 text-primary" strokeWidth={3} />
        {m.features_hero_chip_booked({ name: m.features_sample_client_first(), time: "18:30" })}
      </FloatChip>
    </div>
  </div>
);

/** A phone standing in the tile's picture area, its top showing and the rest cropped by the card. */
const croppedPhone = "absolute top-0 left-1/2 w-[min(300px,82%)] -translate-x-1/2";

/**
 * What the coach app does, tile by tile, each on the hardware it runs on: the coach's laptop and
 * phone, and the client's phone beside them where the client's side is the point.
 */
export const FeaturesShowcase = () => (
  <Section>
    <div className="mb-10 max-w-[780px] px-1">
      <Eyebrow>{m.features_showcase_eyebrow()}</Eyebrow>
      <h2 className={cn(displayTitle, "mt-5")}>
        {m.features_showcase_title_lead()} <Accent>{m.features_showcase_title_tail()}</Accent>
      </h2>
      <p className="mt-5 max-w-[56ch] text-lg leading-[1.55] text-pretty text-muted-foreground">
        {m.features_showcase_lede()}
      </p>
    </div>
    <div className="grid gap-3 md:grid-cols-2">
      <Stage
        side
        title={m.features_plan_title()}
        lead={m.features_plan_lead()}
        body={m.features_plan_body()}
        className="md:col-span-2"
        artClassName="flex items-center justify-center px-5 pt-2 pb-12 lg:py-16 lg:pr-12 lg:pl-0"
      >
        <div className="relative w-full max-w-[800px] pr-[10%]">
          <Laptop>
            <PlanDesk />
          </Laptop>
          <Phone className="absolute right-0 -bottom-[7%] w-[20%]">
            <ClientPlanScreen />
          </Phone>
          <FloatChip className="bottom-[16%] -left-[3%] max-lg:hidden">
            <Send className="size-4 text-primary" />
            {m.features_plan_chip({ name: m.features_sample_client_first() })}
          </FloatChip>
        </div>
      </Stage>

      <Stage lead={m.features_pay_lead()} body={m.features_pay_body()} className="h-[680px] md:h-[620px]">
        <PassesArt />
      </Stage>

      <Stage lead={m.features_client_lead()} body={m.features_client_body()} className="h-[680px] md:h-[620px]" fade>
        <Phone className={croppedPhone}>
          <ClientScreen />
        </Phone>
      </Stage>

      <Stage lead={m.features_directory_lead()} body={m.features_directory_body()} className="h-[680px] md:h-[620px]">
        <DirectoryMap />
      </Stage>

      <Stage
        lead={m.features_watch_lead()}
        body={m.features_watch_body()}
        badge={<SoonBadge>{m.features_soon()}</SoonBadge>}
        className="h-[680px] md:h-[620px]"
      >
        <Watch className="absolute top-1/2 left-1/2 w-[240px] -translate-x-1/2 -translate-y-[47%]">
          <NextClientFace />
        </Watch>
      </Stage>

      <Stage
        side
        title={m.features_live_title()}
        lead={m.features_live_lead()}
        body={m.features_live_body()}
        className="md:col-span-2"
        artClassName="flex items-center justify-center px-5 pt-2 pb-12 lg:py-16 lg:pr-12 lg:pl-0"
      >
        <div className="relative w-full max-w-[800px] pr-[10%]">
          <Laptop>
            <LiveDesk />
          </Laptop>
          <Phone className="absolute right-0 -bottom-[7%] w-[20%]">
            <ClientLiveScreen />
          </Phone>
        </div>
      </Stage>

      <Stage lead={m.features_finance_lead()} body={m.features_finance_body()} className="h-[680px] md:h-[620px]" fade>
        <Phone className={croppedPhone}>
          <FinanceScreen />
        </Phone>
      </Stage>

      <Stage lead={m.features_events_lead()} body={m.features_events_body()} className="h-[680px] md:h-[620px]" fade>
        <Phone className={croppedPhone}>
          <EventsScreen />
        </Phone>
      </Stage>
    </div>
  </Section>
);
